import { useEffect, useRef, useState } from 'react';

// How long a step's text has to sit unchanged before we assume the backend
// has gone quiet (a slow tool call, a long LLM turn, ...) and start showing
// a fill message instead of the stale real text.
const IDLE_THRESHOLD_MS = 8000;
// How long each displayed fill message sticks around once idle — randomized
// per cycle (not a fixed cadence) so the rotation doesn't read as a metronome.
const FILL_CYCLE_MIN_MS = 5000;
const FILL_CYCLE_MAX_MS = 8000;
// Re-render cadence while idle — only needs to be fine-grained enough that
// the cycle rotation feels on-beat.
const TICK_MS = 250;

const FILL_MESSAGES = [
    'Cooking up insights…',
    'Grinding through your data…',
    'Crunching the numbers…',
    'Untangling the data threads…',
    'Polishing the insights…',
    'Connecting the dots…',
    'Warming up the analysis…',
    'Digging a little deeper…',
    'Making sense of the noise…',
    'Getting further…',

    // Analysis / thinking
    'Following the trail…',
    'Chasing down a hunch…',
    'Reading between the rows…',
    'Weighing the evidence…',
    'Second-guessing the first guess…',
    'Sanity-checking the math…',
    'Zooming out for context…',
    'Zooming in on the details…',
    'Cross-referencing everything…',
    'Testing a theory…',
    'Circling the answer…',
    'Chewing on this one…',
    'Turning it over…',
    'Checking the assumptions…',
    'Looking for the pattern…',
    'Comparing like with like…',
    'Tracing it back to the source…',

    // Data wrangling
    'Herding the numbers…',
    'Sorting signal from noise…',
    'Lining up the columns…',
    'Smoothing out the rough edges…',
    'Tidying up loose ends…',
    'Filling in the gaps…',
    'Stitching the pieces together…',
    'Sifting through the pile…',
    'Wrangling the outliers…',
    'Reconciling the numbers…',
    'Lining up the timelines…',
    'Squaring the totals…',
    'Sweeping up the stragglers…',
    'Flattening the messy bits…',

    // Insight / synthesis
    'Sharpening the takeaway…',
    'Finding the story in the data…',
    'Boiling it down…',
    'Assembling the big picture…',
    'Turning data into something useful…',
    'Drawing the conclusions…',
    'Putting it into words…',
    'Trimming the fat…',
    'Deciding what actually matters…',
    'Choosing the right words…',
    'Getting to the point…',

    // Progress-flavored
    'Almost got it…',
    'Wrapping it up neatly…',
    'Adding the finishing touches…',
    'Last pass over the details…',
    'Nearly there…',

    // Playful
    'Consulting the spreadsheets…',
    "Doing the boring part so you don't have to…",
    'Thinking harder than strictly necessary…',
    'Arguing with myself about this one…',
    "This one's interesting…",
    'Not stalling, promise…',
    'Resisting the urge to over-explain…',
    'Being thorough, not slow…'
];

// Deterministic pseudo-random float in [0, 1) from an integer seed — same
// seed always gives the same output. Lets every independent hook instance
// (accordion title, steps-list row, ...) compute the same "random" cycle
// lengths and phrase order for the "same" underlying step without sharing
// any state.
function pseudoRandom(seed: number): number {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
}

// Turns the step's own (frozen, stale) text into an integer seed — so two
// different steps going idle get two different phrase orders/cadences
// instead of both marching through FILL_MESSAGES in the exact same
// 0, 1, 2, 3, ... order every single time, which is what reads as a
// precalculated list rather than organic filler. djb2 string hash.
function hashSeed(text: string): number {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) {
        hash = (hash * 33) ^ text.charCodeAt(i);
    }
    return hash >>> 0;
}

// Cycle `index`'s length in ms for this `seed` — randomized per cycle
// within [FILL_CYCLE_MIN_MS, FILL_CYCLE_MAX_MS) instead of a fixed cadence,
// so the rotation doesn't read as a metronome ticking on the beat.
function cycleDurationMs(seed: number, index: number): number {
    return (
        FILL_CYCLE_MIN_MS +
        pseudoRandom(seed + index * 104729) *
            (FILL_CYCLE_MAX_MS - FILL_CYCLE_MIN_MS)
    );
}

// Walks cumulative (randomized) cycle boundaries to find which cycle
// `elapsedMs` currently falls in. Cycles are only a few seconds each, so
// even minutes of idle time means a handful of iterations — negligible.
function cycleIndexForElapsed(seed: number, elapsedMs: number): number {
    let boundary = 0;
    let index = 0;
    while (true) {
        boundary += cycleDurationMs(seed, index);
        if (boundary > elapsedMs) return index;
        index++;
    }
}

// Picks a FILL_MESSAGES index for this cycle — shuffled per `seed` rather
// than walking the list in order, and nudged off the previous cycle's pick
// so the same phrase never shows twice back to back.
function messageIndexForCycle(seed: number, cycleIndex: number): number {
    const pick = (i: number) =>
        Math.floor(pseudoRandom(seed + i * 7919) * FILL_MESSAGES.length);
    const index = pick(cycleIndex);
    if (cycleIndex > 0 && index === pick(cycleIndex - 1)) {
        return (index + 1) % FILL_MESSAGES.length;
    }
    return index;
}

/**
 * Returns `text` unchanged as long as it keeps getting updated. Once
 * `isActive` is true and `text` hasn't changed for IDLE_THRESHOLD_MS, starts
 * cycling through FILL_MESSAGES instead — so a long-running step doesn't
 * read as stuck. Snaps back to the real text the instant it changes again.
 *
 * The displayed fill phrase is derived from wall-clock time since the step
 * went idle rather than a per-instance counter, so every component calling
 * this hook for the "same" underlying step (e.g. the accordion title and its
 * steps-list row) lands on the same phrase at the same time without sharing
 * any state.
 */
export function useIdleFillMessage(text: string, isActive: boolean): string {
    const lastTextRef = useRef(text);
    const prevIsActiveRef = useRef(isActive);
    const idleSinceRef = useRef(Date.now());
    const [, forceTick] = useState(0);

    useEffect(() => {
        const textChanged = text !== lastTextRef.current;
        const justActivated = isActive && !prevIsActiveRef.current;
        if (textChanged || justActivated) {
            idleSinceRef.current = Date.now();
        }
        lastTextRef.current = text;
        prevIsActiveRef.current = isActive;
    }, [text, isActive]);

    useEffect(() => {
        if (!isActive) return;
        const interval = setInterval(() => forceTick((n) => n + 1), TICK_MS);
        return () => clearInterval(interval);
    }, [isActive]);

    if (!isActive) return text;

    const idleMs = Date.now() - idleSinceRef.current;
    if (idleMs < IDLE_THRESHOLD_MS) return text;

    const seed = hashSeed(lastTextRef.current);
    const cycleIndex = cycleIndexForElapsed(seed, idleMs - IDLE_THRESHOLD_MS);
    return FILL_MESSAGES[messageIndexForCycle(seed, cycleIndex)];
}
